import { IconCorporateFareBlue } from "assets/svg";
import {
  formatWeightUnit,
  formatWeightValue,
} from "common/helper/quantity.helper";
import { FILE_BASE_URL } from "config/apiConfig";
import React from "react";
import { RankingItem, RankingMyRank } from "reducers/rankingApi";
import RankBadge from "./RankBadge";

interface RankingMyRankBarProps {
  myRank: RankingMyRank;
}

const getFileUrl = (path: string) => {
  if (/^https?:\/\//.test(path)) return path;
  return `${FILE_BASE_URL}/${path.replace(/^\/+/, "")}`;
};

const isRankedItem = (
  myRank: RankingMyRank,
): myRank is RankingMyRank & {
  item: RankingItem;
} => myRank.ranked && myRank.item.rank !== null;

const CompanyIdentity = ({ myRank }: RankingMyRankBarProps) => {
  const { item } = myRank;

  return (
    <div className="flex min-w-0 items-center gap-2.5">
      {item.company_logo_url ? (
        <img
          src={getFileUrl(item.company_logo_url)}
          alt=""
          className="h-9 w-9 shrink-0 rounded-full bg-[#eef5fa] object-cover"
        />
      ) : (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#eef5fa]">
          <IconCorporateFareBlue
            aria-hidden="true"
            className="h-5 w-5 text-primary"
          />
        </span>
      )}
      <span className="min-w-0 break-words text-sm font-bold">
        {item.company_name || "-"}
      </span>
    </div>
  );
};

const MyRankKilogramValue = ({
  value,
  compact = false,
}: {
  value: number;
  compact?: boolean;
}) => (
  <span
    className={
      compact
        ? "inline-flex min-w-0 items-baseline gap-1"
        : "grid w-[8.5rem] grid-cols-[6rem_max-content] items-baseline gap-1"
    }
  >
    <span className={compact ? "min-w-0 break-all" : "text-right"}>
      {formatWeightValue(value)}
    </span>
    <span className="shrink-0">{formatWeightUnit(value)}</span>
  </span>
);

const RankingMyRankBar = ({ myRank }: RankingMyRankBarProps) => {
  const ranked = isRankedItem(myRank);

  return (
    <section
      aria-label="自社の順位"
      className="border-t-2 border-primary bg-[#eef5fa] shadow-[0_-3px_8px_rgba(0,41,89,0.16)]"
    >
      <div className="flex flex-col gap-3 px-4 py-3 md:hidden">
        <div className="grid grid-cols-[4.25rem_minmax(0,1fr)] items-center gap-3">
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs font-bold text-primary">自社順位</span>
            {ranked ? (
              <RankBadge rank={myRank.item.rank} />
            ) : (
              <span className="text-sm font-bold text-primary">
                {myRank.label || "順位なし"}
              </span>
            )}
          </div>
          <CompanyIdentity myRank={myRank} />
        </div>
        <div className="grid grid-cols-2 gap-2 rounded bg-white/80 px-3 py-2 text-xs text-primary">
          <div>
            <p className="font-bold">構造スコア</p>
            <p className="mt-1 min-w-0 break-all font-['Audiowide'] text-base leading-none">
              {ranked ? myRank.item.total_xp.toLocaleString("en-US") : "-"}
            </p>
          </div>
          <div>
            <p className="font-bold">循環量</p>
            <p className="mt-1 font-['Audiowide'] text-sm leading-none">
              {ranked ? (
                <MyRankKilogramValue
                  value={myRank.item.total_volume_kg}
                  compact
                />
              ) : (
                "-"
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="hidden overflow-x-auto md:block">
        <div className="grid min-h-[66px] min-w-[850px] grid-cols-[90px_minmax(0,1fr)_200px_140px_170px] items-center text-sm">
          <div className="flex flex-col items-center justify-center gap-1 px-[15px]">
            <span className="text-xs font-bold text-primary">自社順位</span>
            {ranked ? (
              <RankBadge rank={myRank.item.rank} />
            ) : (
              <span className="font-bold text-primary">
                {myRank.label || "順位なし"}
              </span>
            )}
          </div>
          <div className="min-w-0 px-[15px]">
            <CompanyIdentity myRank={myRank} />
          </div>
          <div className="px-[15px] font-['Audiowide'] text-lg">
            {ranked ? `Lv.${myRank.item.level}` : "-"}
          </div>
          <div className="px-[15px] font-['Audiowide'] text-lg">
            {ranked ? myRank.item.total_xp.toLocaleString("en-US") : "-"}
          </div>
          <div className="px-[15px] font-['Audiowide'] text-lg">
            <div className="flex justify-center">
              {ranked ? (
                <MyRankKilogramValue value={myRank.item.total_volume_kg} />
              ) : (
                "-"
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RankingMyRankBar;
